import { useTranslate, useMany } from '@refinedev/core';
import { List, DeleteButton } from '@refinedev/antd';
import { Table, Space, Form, Row, Col, Input } from 'antd';
import { LinkOutlined, SearchOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import type { OAuthAccountOut, UserOut } from '@/api/generated/schemas';
import { OAuthProviderTag } from '@/components/iam/tags';
import { useTableSearch } from '@/hooks/useTableSearch';
import { useTableRowNavigateToShow } from '@/hooks/useTableRowNavigateToShow';
import { useAdminTable } from '@/hooks/useAdminTable';
import type { CrudFilter } from '@refinedev/core';
import { SelectFilter } from '@/components/common/SelectFilter';
import { TextFilter } from '@/components/common/TextFilter';
import { DateRangeFilter } from '@/components/common/DateRangeFilter';
import { isFieldFiltered } from '@/utils/filters';
import { getSortOrder } from '@/utils/sorters';

export const OAuthAccountList = () => {
  const t = useTranslate();
  const rowNav = useTableRowNavigateToShow();
  const { onSearch, createDebouncedSubmit } = useTableSearch();

  const { tableProps, searchFormProps, sorters, setFilters, filters } =
    useAdminTable<OAuthAccountOut>({
      pagination: { pageSize: 20 },
      resource: 'iam.oauth_accounts',
      onSearch,
      sorters: {
        initial: [{ field: 'created_at', order: 'desc' }],
      },
    });

  const debouncedSubmit = createDebouncedSubmit(searchFormProps.form);

  const userIds = [
    ...new Set(
      (tableProps.dataSource ?? []).map((r) => r.user_id).filter(Boolean)
    ),
  ];

  const { result: usersResult } = useMany<UserOut>({
    resource: 'iam.users',
    ids: userIds,
    queryOptions: { enabled: userIds.length > 0 },
  });

  const usersById = Object.fromEntries(
    (usersResult?.data ?? []).map((u: UserOut) => [u.id, u])
  );

  return (
    <List>
      <Form {...searchFormProps} layout="vertical">
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={16} md={12} lg={8}>
            <Form.Item name="q" style={{ marginBottom: 0 }}>
              <Input
                placeholder={t('iam.oauth_accounts.search.placeholder')}
                prefix={<SearchOutlined />}
                allowClear
                onChange={(e) => debouncedSubmit(e.target.value)}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Table {...rowNav('iam.oauth_accounts', tableProps)} rowKey="id">
        <Table.Column
          dataIndex="id"
          title={t('base.fields.id')}
          sorter
          sortOrder={getSortOrder(sorters, 'id')}
        />

        <Table.Column
          dataIndex="user_id"
          title={t('iam.oauth_accounts.fields.user_id')}
          render={(userId: number) => {
            const user = usersById[userId];
            return (
              <Space size={8}>
                <span>{user ? (user.email ?? user.username) : userId}</span>
                <Link to={`/iam/users/${userId}`}>
                  <LinkOutlined />
                </Link>
              </Space>
            );
          }}
          filtered={isFieldFiltered(filters as CrudFilter[], 'user_id')}
          filterDropdown={({ confirm, clearFilters }) => (
            <TextFilter
              field="user_id"
              placeholder={t('iam.oauth_accounts.fields.user_id')}
              width={120}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="oauth_name"
          title={t('iam.oauth_accounts.fields.oauth_name')}
          render={(provider: string) => (
            <OAuthProviderTag provider={provider} />
          )}
          filtered={isFieldFiltered(filters as CrudFilter[], 'oauth_name')}
          filterDropdown={({ confirm, clearFilters }) => (
            <SelectFilter
              field="oauth_name"
              minWidth={150}
              placeholder={t('iam.oauth_accounts.filter.oauth_name')}
              options={[
                {
                  label: t('iam.oauth_accounts.providers.google'),
                  value: 'google',
                },
                {
                  label: t('iam.oauth_accounts.providers.apple'),
                  value: 'apple',
                },
              ]}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="account_email"
          title={t('iam.oauth_accounts.fields.account_email')}
        />

        <Table.Column
          dataIndex="account_id"
          title={t('iam.oauth_accounts.fields.account_id')}
          ellipsis
        />

        <Table.Column
          dataIndex="created_at"
          title={t('base.fields.created_at')}
          sorter
          sortOrder={getSortOrder(sorters, 'created_at')}
          filtered={isFieldFiltered(filters as CrudFilter[], 'created_at')}
          filterDropdown={({ confirm, clearFilters }) => (
            <DateRangeFilter
              field="created_at"
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
          render={(value: string) =>
            value ? new Date(value).toLocaleString() : '-'
          }
        />

        <Table.Column
          title={t('actions.column')}
          dataIndex="actions"
          render={(_, record: OAuthAccountOut) => (
            <Space>
              <DeleteButton hideText size="small" recordItemId={record.id} />
            </Space>
          )}
        />
      </Table>
    </List>
  );
};
